import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bus } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { trustedOperatorTypeLabel } from '@/constants/labels';
import {
  getTrustedOperators,
  getTrustedOperatorSubscriptions,
  subscribeTrustedOperator,
  unsubscribeTrustedOperator,
} from '@/services/api.client';

interface Props {
  propertyId: string;
}

export function TrustedOperatorAlerts({ propertyId }: Props) {
  const queryClient = useQueryClient();

  const operatorsQuery = useQuery({
    queryKey: ['resident', 'trusted-operators', propertyId],
    queryFn: () => getTrustedOperators(propertyId),
    enabled: !!propertyId,
  });

  const subscriptionsQuery = useQuery({
    queryKey: ['resident', 'trusted-operator-subscriptions', propertyId],
    queryFn: () => getTrustedOperatorSubscriptions(propertyId),
    enabled: !!propertyId,
  });

  const subscribeMutation = useMutation({
    mutationFn: (operatorId: string) => subscribeTrustedOperator(propertyId, operatorId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['resident', 'trusted-operator-subscriptions', propertyId],
      });
    },
    onError: () => toast.error('Couldn\u2019t update your alert. Please try again.'),
  });

  const unsubscribeMutation = useMutation({
    mutationFn: (operatorId: string) => unsubscribeTrustedOperator(propertyId, operatorId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['resident', 'trusted-operator-subscriptions', propertyId],
      });
    },
    onError: () => toast.error('Couldn\u2019t update your alert. Please try again.'),
  });

  const operators = operatorsQuery.data ?? [];
  const subscriptions = subscriptionsQuery.data ?? [];
  const subscribedIds = new Set(subscriptions.map((s) => s.trusted_operator_id));

  const handleToggle = (operatorId: string, isSubscribed: boolean) => {
    if (isSubscribed) {
      unsubscribeMutation.mutate(operatorId);
    } else {
      subscribeMutation.mutate(operatorId);
    }
  };

  // Don't render the section if there are no operators
  if (operatorsQuery.isSuccess && operators.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bus className="size-5" />
          Trusted Operator Alerts
        </CardTitle>
        <CardDescription>Get notified when a trusted operator enters the estate.</CardDescription>
      </CardHeader>
      <CardContent>
        {operatorsQuery.isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {operatorsQuery.isError && (
          <p role="alert" className="text-sm text-muted-foreground">
            Couldn&apos;t load trusted operators. Please try again later.
          </p>
        )}

        {operators.length > 0 && (
          <div className="space-y-3">
            {operators.map((op) => {
              const isSubscribed = subscribedIds.has(op.id);
              return (
                <div
                  key={op.id}
                  className="flex items-center justify-between py-2 border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="text-sm font-medium">{op.name}</p>
                      <Badge variant="secondary" className="text-xs mt-0.5">
                        {trustedOperatorTypeLabel(op.operator_type)}
                      </Badge>
                    </div>
                  </div>
                  <Switch
                    checked={isSubscribed}
                    onCheckedChange={() => handleToggle(op.id, isSubscribed)}
                    aria-label={`Notify me when ${op.name} enters`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
